/**
 * Tag count pill (AC1 / AC8) — opens Tags Details when clicked.
 */

import React, { useState } from "react";
import Box from "@mui/material/Box";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { AutoTagsViewAllDialog } from "./AutoTagsViewAllDialog";
import {
  countDisplayedTags,
  type RegulationAutoTagGroup,
} from "../../../services/regulationAutoTags";

export type TagsCountIndicatorProps = Readonly<{
  groups: RegulationAutoTagGroup[];
  /** Manual categories/tags tree shown under merged groups in Tags Details. */
  categoryItems?: readonly unknown[];
  regulationId?: string | null;
  regulationTitle?: string | null;
  /** Optional test id override (detail vs card). */
  testId?: string;
  /** Card view still shows "0 Tags" and opens the details modal. */
  showZero?: boolean;
}>;

export function TagsCountIndicator({
  groups,
  categoryItems,
  regulationId,
  regulationTitle,
  testId = "tags-count-indicator",
  showZero = false,
}: TagsCountIndicatorProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const tagCount = countDisplayedTags(groups, categoryItems);

  if (tagCount <= 0 && !showZero) return null;

  return (
    <>
      <Box
        component="button"
        type="button"
        data-testid={testId}
        aria-label={`${tagCount} Tags`}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDialogOpen(true);
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          height: 28,
          px: 1.5,
          ml: 0.75,
          border: "none",
          borderRadius: "999px",
          bgcolor: "#E7ECFC",
          color: "#295EF1",
          fontSize: "13px",
          fontWeight: 500,
          cursor: "pointer",
          fontFamily: "inherit",
          flexShrink: 0,
          alignSelf: "center",
          "&:hover": {
            bgcolor: "#D6E0FB",
          },
        }}
      >
        <LocalOfferOutlinedIcon sx={{ fontSize: 12 }} />
        {tagCount} Tags
      </Box>
      <AutoTagsViewAllDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        groups={groups}
        categoryItems={categoryItems}
        regulationId={regulationId}
        regulationTitle={regulationTitle}
      />
    </>
  );
}
